import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import ParetoChart from "@/components/dashboard/ParetoChart";

function Panel({ n, title, children, right, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
          <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ParetoPage() {
  const { params } = useFilters();
  const [pareto, setPareto] = useState({ items: [], total: 0 });

  useEffect(() => {
    (async () => {
      const p = await api.get("/metrics/pareto", { params });
      setPareto(p.data);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const cutoff80 = pareto.items.findIndex(i => i.cumulative_percent >= 80);
  const vitalFew = cutoff80 >= 0 ? pareto.items.slice(0, cutoff80 + 1) : pareto.items;

  return (
    <div className="space-y-6 pb-8" data-testid="detail-pareto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total Subsystems</div>
          <div className="text-3xl font-black text-slate-900 mt-1">{pareto.items.length}</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Vital Few (80% Rule)</div>
          <div className="text-3xl font-black text-[#ef4444] mt-1">{vitalFew.length}</div>
          <div className="text-xs text-slate-500 mt-1">causing 80% of failures</div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total Failure Events</div>
          <div className="text-3xl font-black text-[#1e40af] mt-1">{pareto.total.toLocaleString()}</div>
        </div>
      </div>

      <Panel n="1" title="Pareto Chart – Failure Events by Subsystem">
        <ParetoChart items={pareto.items} />
      </Panel>

      <Panel n="2" title="Ranking Table">
        <div className="overflow-x-auto trams-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="text-left font-semibold pb-3">Rank</th>
                <th className="text-left font-semibold pb-3">Subsystem</th>
                <th className="text-right font-semibold pb-3">Failure Count</th>
                <th className="text-right font-semibold pb-3">Downtime (Min)</th>
                <th className="text-right font-semibold pb-3">Cumulative %</th>
                <th className="text-left font-semibold pb-3">Priority</th>
              </tr>
            </thead>
            <tbody>
              {pareto.items.map((it, i) => (
                <tr key={it.subsystem} className="border-b border-slate-100">
                  <td className="py-3 font-black text-slate-400 tabular-nums">#{i + 1}</td>
                  <td className="py-3 font-semibold text-slate-900">{it.subsystem}</td>
                  <td className="py-3 text-right tabular-nums">{it.count.toLocaleString()}</td>
                  <td className="py-3 text-right tabular-nums">{Math.round(it.downtime_min).toLocaleString()}</td>
                  <td className="py-3 text-right tabular-nums font-semibold">{it.cumulative_percent}%</td>
                  <td className="py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                      it.cumulative_percent <= 80
                        ? "bg-red-100 text-red-700 border-red-200"
                        : it.cumulative_percent <= 95
                        ? "bg-amber-100 text-amber-700 border-amber-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}>
                      {it.cumulative_percent <= 80 ? "Vital Few" : it.cumulative_percent <= 95 ? "Useful Many" : "Trivial"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
